/**
 * How long a turn has been running, measured from the turn instead of the view.
 *
 * The elapsed counter used to start at the moment the component mounted and add
 * a base it could never obtain: the base came from `getMessageElapsedMs`, which
 * returns nothing while a turn is still streaming — every duration it reads is
 * only written when the turn ends. So the base was always 0, and switching
 * conversations or reloading the page restarted a generation that had been
 * running for a minute at "用时 0秒".
 *
 * Both anchors below survive a remount: `gatewayAcceptedAt` is the server's own
 * record of when it accepted the turn, and `timestamp` is stored on the message.
 */

export interface TurnElapsedSource {
  timestamp?: number;
  generationMetrics?: { gatewayAcceptedAt?: number };
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** The instant this turn started, preferring the server's own record of it. */
export function resolveTurnStartedAt(message: TurnElapsedSource): number | undefined {
  const accepted = message.generationMetrics?.gatewayAcceptedAt;
  if (isFiniteNumber(accepted)) return accepted;
  return isFiniteNumber(message.timestamp) ? message.timestamp : undefined;
}

/** The stages during which the turn is waiting on the engineer, not working. */
const HUMAN_WAIT_STAGES = ['requirementConfirmation', 'planReview'] as const;

interface WaitingStageBuffer {
  started?: boolean;
  done?: boolean;
  startedAt?: number;
  endedAt?: number;
}

export interface TurnWaitSource {
  stageBuffers?: Partial<Record<(typeof HUMAN_WAIT_STAGES)[number], WaitingStageBuffer | undefined>>;
}

/**
 * How long this turn has spent waiting on the engineer: the confirmation cards
 * until they were answered, the plan until it was approved. A wait still open
 * counts up to `now`.
 *
 * The turn clock used to run through these: 「已用 6分36秒」 kept counting while
 * the engineer read the plan, so one number mixed how long the model worked
 * with how long a person looked (production, 2026-09-15). The per-step clocks
 * already stop for a blocking step; the turn's own clock did not.
 */
export function resolveHumanWaitMs(message: TurnWaitSource, now: number): number {
  let waited = 0;
  for (const key of HUMAN_WAIT_STAGES) {
    const buffer = message.stageBuffers?.[key];
    if (!buffer?.started || !isFiniteNumber(buffer.startedAt)) continue;
    const endedAt = isFiniteNumber(buffer.endedAt) ? buffer.endedAt : (buffer.done ? buffer.startedAt : now);
    waited += Math.max(0, endedAt - buffer.startedAt);
  }
  return waited;
}

export function resolveDisplayElapsedMs(input: {
  isActive: boolean;
  finalElapsedMs?: number;
  turnStartedAt?: number;
  /** Time spent waiting on the engineer so far, left out of the count. */
  waitingMs?: number;
  now: number;
}): number | undefined {
  if (!input.isActive) {
    return isFiniteNumber(input.finalElapsedMs) ? input.finalElapsedMs : undefined;
  }

  if (isFiniteNumber(input.turnStartedAt)) {
    // Clamped: the anchor can be the server's clock and the `now` is the
    // browser's, and a negative duration reads worse than a zero one.
    return Math.max(0, input.now - input.turnStartedAt - (input.waitingMs ?? 0));
  }

  return isFiniteNumber(input.finalElapsedMs) ? input.finalElapsedMs : 0;
}

export interface TurnFinalElapsedSource extends TurnElapsedSource, TurnWaitSource {
  isStreaming?: boolean;
  generationMetrics?: {
    gatewayAcceptedAt?: number;
    totalEndToEndMs?: number;
    workerTotalMs?: number;
    clientTotalMs?: number;
  };
  progressState?: { updatedAt?: number };
}

/**
 * How long the finished turn took — measured end to end, not job by job.
 *
 * The order below settled in two steps, and both steps were about the same
 * mistake: offering a span narrower than the turn as the turn's duration. See
 * the comment inside for the anchors that came first in the end.
 *
 * `clientTotalMs` is one browser tab's stopwatch, and a tab only measures the
 * part of the turn it was present for. Reload the page mid-run, open the
 * conversation in a second tab, or let the stream drop and re-attach, and that
 * stopwatch reads short — while the server's own `totalEndToEndMs` covers a
 * whole job. Ranked first, the tab's number won.
 *
 * Measured live 2026-09-11: the server recorded 239,353 ms for a turn accepted
 * at 08:11:51 and finished at 08:15:50, and the turn footer read 「用时 1分10秒」
 * — directly beneath 「方案规划 · 1m09s」 and 「计划审批 · 15s」, a total shorter
 * than two of its own steps.
 *
 * `modelLatencyMs` used to end this list. It is how long one model call took,
 * never how long the turn took, and offering it as a turn duration is the same
 * mistake one field further down. A turn with no metrics at all is measured
 * from its own timestamps instead.
 */
export function resolveFinalElapsedMs(message: TurnFinalElapsedSource): number | undefined {
  // The turn's own two ends, when it has them.
  //
  // Every `measured` value below covers one job. A turn is not always one job:
  // a turn with confirmation cards asks, waits for the engineer, and then runs,
  // and that is two gateway jobs on one message. So the totals answer a
  // narrower question than the one the footer asks.
  //
  // 2026-09-15 线上：
  //
  //   gatewayAcceptedAt        19:23:46
  //   progressState.updatedAt  19:48:02   → 24分16秒
  //   计数器                    已用 24分6秒
  //   totalEndToEndMs          19分32秒     ← 页脚显示的，少 4分44秒
  //   clientTotalMs            24分18秒
  //
  // The footer read `clientTotalMs` before and was 31 seconds short; ranking
  // `totalEndToEndMs` first made it 4m44s short. The ranking was never the
  // problem. Both anchors here are the server's own record — the acceptance
  // instant and the last progress frame — so they survive a reload the way a
  // tab stopwatch does not, and between them they span the whole turn, which
  // is what the counter above the footer has been measuring all along.
  const endAt = message.progressState?.updatedAt;
  const startedAt = resolveTurnStartedAt(message);
  if (!message.isStreaming && isFiniteNumber(startedAt) && isFiniteNumber(endAt) && endAt > startedAt) {
    // Net of the time spent waiting on the engineer — the same subtraction the
    // live counter makes, so the two numbers agree when the turn settles.
    return Math.max(0, endAt - startedAt - resolveHumanWaitMs(message, endAt));
  }

  // No anchors: a turn stored before progress state existed, or one still
  // running. Then the recorded totals are the best account there is, ranked as
  // before — the server's over one tab's.
  const metrics = message.generationMetrics;
  const measured = [
    metrics?.totalEndToEndMs,
    metrics?.workerTotalMs,
    metrics?.clientTotalMs,
  ].find(value => isFiniteNumber(value) && value >= 0);
  if (isFiniteNumber(measured)) return measured;

  return undefined;
}
